import { ref, onMounted, onUnmounted } from 'vue';
import { useCSPCompliantStyles } from './useCSPCompliantStyles';

export function useSwipeGestures(targetElement, options = {}) {
  const {
    threshold = 80,        // Minimum distance for swipe
    velocityThreshold = 0.5, // Minimum velocity
    maxTime = 500,         // Maximum time for swipe
    preventScrolling = true, // Prevent page scrolling during swipe
    dismissThreshold = 100,  // Threshold for dismissing the element
    onDismiss = null        // Callback function for dismissing the element
  } = options;

  const isSwipeActive = ref(false);
  const swipeDirection = ref(null);
  
  // Use CSP-compliant styles
  const { addStyleElement } = useCSPCompliantStyles();

  let touchStartX = 0;
  let touchStartY = 0;
  let touchStartTime = 0;
  let isDragging = false;

  // Add CSS styles for swipe gestures using CSP-compliant method
  function addSwipeStyles() {
    const css = `
      .swipe-no-transition {
        transition: none !important;
      }
      
      .swipe-transition {
        transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1) !important;
      }
      
      .swipe-dismiss {
        transform: translateY(100%) !important;
        opacity: 0 !important;
      }
      
      .swipe-reset {
        transform: translateY(0) !important;
        opacity: 1 !important;
      }
    `;
    
    addStyleElement('swipe-gesture-styles', css);
  }

  // Initialize styles
  addSwipeStyles();

  const handleTouchStart = (e) => {
    if (!targetElement.value) return;
    
    touchStartX = e.touches[0].clientX;
    touchStartY = e.touches[0].clientY;
    touchStartTime = Date.now();
    isDragging = true;
    isSwipeActive.value = true;
    
    // Remove transition during drag
    targetElement.value.classList.add('swipe-no-transition');
    
    // Note: Cannot preventDefault in passive event listener
    // Prevention of default behavior is handled in touchmove
  };

  const handleTouchMove = (e) => {
    if (!isSwipeActive.value || !targetElement.value || !isDragging) return;
    
    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStartX;
    const deltaY = touch.clientY - touchStartY;
    
    // Determine swipe direction based on dominant axis
    if (Math.abs(deltaY) > Math.abs(deltaX)) {
      // Vertical swipe
      swipeDirection.value = deltaY > 0 ? 'down' : 'up';
      
      if (preventScrolling && Math.abs(deltaY) > 10) {
        e.preventDefault();
      }
      
      // Add visual feedback for vertical swipes (only for downward swipes to dismiss)
      if (swipeDirection.value === 'down' && deltaY > 0) {
        const opacity = Math.max(0.3, 1 - (deltaY / 200));
        const translateY = Math.min(deltaY * 0.5, 100);
        
        targetElement.value.style.transform = `translateY(${translateY}px)`;
        targetElement.value.style.opacity = opacity;
      }
    } else {
      // Horizontal swipe
      swipeDirection.value = deltaX > 0 ? 'right' : 'left';
      
      if (preventScrolling && Math.abs(deltaX) > 10) {
        e.preventDefault();
      }
      
      // Add visual feedback for horizontal swipes
      const translateX = deltaX * 0.3; // Reduced movement for resistance effect
      targetElement.value.style.transform = `translateX(${translateX}px)`;
    }
  };

  const handleTouchEnd = (e) => {
    if (!targetElement.value || !isDragging) return;
    
    isDragging = false;
    isSwipeActive.value = false;
    
    const endTime = Date.now();
    const timeDiff = endTime - touchStartTime;
    
    // Calculate actual movement
    const endX = e.changedTouches?.[0]?.clientX || touchStartX;
    const endY = e.changedTouches?.[0]?.clientY || touchStartY;
    const deltaX = endX - touchStartX;
    const deltaY = endY - touchStartY;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    const velocity = distance / timeDiff; // pixels per millisecond
    
    // Re-enable transitions
    targetElement.value.classList.remove('swipe-no-transition');
    targetElement.value.classList.add('swipe-transition');
    
    // Determine if swipe should trigger dismiss (only for downward swipes)
    const shouldDismiss = (
      swipeDirection.value === 'down' && 
      (deltaY > dismissThreshold || velocity > velocityThreshold) &&
      deltaY > 0
    );
    
    if (shouldDismiss) {
      // Dismiss
      targetElement.value.classList.add('swipe-dismiss');
      
      setTimeout(() => {
        if (onDismiss) onDismiss();
      }, 300);
    } else {
      // Reset position
      targetElement.value.style.transform = '';
      targetElement.value.style.opacity = '';
      targetElement.value.classList.add('swipe-reset');
      targetElement.value.classList.remove('swipe-dismiss');
    }
    
    // Clean up classes after animation
    setTimeout(() => {
      if (targetElement.value) {
        targetElement.value.classList.remove('swipe-transition', 'swipe-reset');
      }
    }, 300);
  };

  const setupSwipeListeners = () => {
    // Use passive listeners for better performance, handle preventDefault conditionally
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false }); // Keep non-passive for preventDefault
    document.addEventListener('touchend', handleTouchEnd, { passive: true });
  };

  const cleanupSwipeListeners = () => {
    document.removeEventListener('touchstart', handleTouchStart);
    document.removeEventListener('touchmove', handleTouchMove);
    document.removeEventListener('touchend', handleTouchEnd);
  };

  // Auto setup/cleanup on mount/unmount
  onMounted(() => {
    setupSwipeListeners();
  });

  onUnmounted(() => {
    cleanupSwipeListeners();
  });

  return {
    isSwipeActive,
    swipeDirection,
    setupSwipeListeners,
    cleanupSwipeListeners
  };
} 